import { useCallback, useEffect } from "react";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { publish } from "@Libs/Events/services/events";
import { IUser } from "@modules/Users/services/users";
import { IRecordEvent, IRecordEventList } from "../types/event.types";
import { useMetadata } from "./useMetadata";

export interface IUseRecordEvent<T extends keyof IRecordEventList> {
  key: T;
  payload?: { data?: Partial<IRecordEvent<T>["payload"]["data"]> } & Partial<
    IRecordEvent<T>["payload"]
  >;
}

const actorTypeFor = (user: IUser) => {
  return user ? (user.admin ? "admin" : "user") : "guest";
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return "mobile";
  }
  return "desktop";
};

const deviceType = getDeviceType();

export const useRecordEvent = (
  { key, payload = {} }: IUseRecordEvent<keyof IRecordEventList>,
  { triggerOnMount = true } = {}
) => {
  const { currentUser, isSuccess: isCurrentUserLoaded } = useCurrentUser();

  const {
    metadata: { context },
  } = useMetadata();

  const recordEvent = useCallback(
    (overrides?: Partial<IRecordEvent<keyof IRecordEventList>["payload"]>) => {
      if (!currentUser) return;

      const { data, metadata } = payload;

      const event: IRecordEvent<keyof IRecordEventList> = {
        key,
        payload: {
          metadata: {
            system: "web",
            actorId: currentUser?.id,
            actorType: actorTypeFor(currentUser),
            userAgent: navigator.userAgent,
            deviceType,
            context,
            ...metadata,
            ...overrides?.metadata,
          },
          data: {
            ...data,
            ...overrides?.data,
          },
        },
      };

      try {
        publish(event);
      } catch (error) {
        console.log({ error });
      }
    },
    [currentUser, context, deviceType, key, payload]
  );

  useEffect(() => {
    if (triggerOnMount) recordEvent();
  }, [currentUser?.id]);

  return { recordEvent, isCurrentUserLoaded };
};
